import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useAtom } from "jotai";
import classNames from "classnames";
import { IntentPredictResult } from "@/model";
import answerRepository from "@/repository/answerRepository";
import { useAgent } from "@/query/agent";
import { singleTestPanelOpenAtom } from "@/atom/singleTestPanel";
import { useJSONViewer, useAgentId } from "@/hook";
import { SearchBar } from "@/component/input";
import { errorToast } from "@/component/Toast";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { CloseIcon } from "@/assets/svgs/icons";
import RefreshIcon from "@/assets/svgs/refresh.svg";

import IntentPredictionCard from "./IntentPredictionCard";
import EntityAnalysisCard from "./EntityAnalysisCard";


const SingleTestPanel: React.FC<{ className?: string}> = ({ className: propsClassName }) => {
    const intl = useIntl();
    const agentId = useAgentId();

    const [predictResult, setPredictResult] = useState<IntentPredictResult>();
    const [isPending, setIsPending] = useState<boolean>(false);

    const [singleTestPanelOpen, setSingleTestPanelOpen] = useAtom(singleTestPanelOpenAtom);
    const { data: agent } = useAgent(agentId);

    const getAnswer = async (query: string) => {
        setIsPending(true);
        answerRepository.predictAnswer(query, agent!.token)
            .then(res => {
                setPredictResult(res.data.data);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "i00004" }));
            })
            .finally(() => {
                setIsPending(false);
            });
    };

    const retryAnswer = async () => {
        if (!predictResult?.ner.query) {
            return;
        }
        await getAnswer(predictResult.ner.query);
    };

    const onClickClose = (e: React.MouseEvent) => {
        e.preventDefault();
        setSingleTestPanelOpen(false);
    };

    useEffect(() => {
        return () => {
            setPredictResult(undefined);
            setIsPending(false);
        };
    }, []);

    if (!singleTestPanelOpen) {
        return null;
    }
    return (
        <div className={classNames("test-panel", propsClassName)}>
            <div className="py-3 flex justify-between items-center rounded-t-[6px]">
                <div className="text-black-500 font-[500] text-[15px]">
                    {intl.formatMessage({ id: 'i00003' })}
                </div>
                <button onClick={onClickClose}>
                    <CloseIcon className="size-4" />
                </button>
            </div>
            <div className="flex items-center">
                <SearchBar onSearch={getAnswer}
                           placeholder={intl.formatMessage({ id: "i00005" })}
                />
            </div>

            {predictResult && !isPending && (
                <div className="mt-2 flex flex-col gap-2">
                    <TaggedQueryCard query={predictResult.ner.query}
                                     result={predictResult}
                                     retryAnswer={retryAnswer}
                    />
                    {!!predictResult?.answers.length ? (
                        <>
                            <EntityAnalysisCard nerResult={predictResult.ner}
                                                synonymResult={predictResult.synonym_result}
                            />
                            {predictResult.answers.map((answer, idx) => (
                                <IntentPredictionCard key={idx}
                                                      answer={answer}
                                                      defaultOpen={idx === 0}
                                />
                            ))}
                        </>
                        ) : (
                            <div className="single-test-card h-[60px]">
                                <div className="single-test-card-head">
                                    {intl.formatMessage({ id: "i00072" })}
                                </div>
                            </div>
                        )
                    }
                </div>
            )}
        </div>
    );
};

interface TaggedQueryCardProps {
    query: string
    result: IntentPredictResult
    retryAnswer: () => Promise<void>
}

const TaggedQueryCard: React.FC<TaggedQueryCardProps> = (props) => {
    const { query, result, retryAnswer } = props;

    const intl = useIntl();
    const jsonViewer = useJSONViewer();

    const onClickResponseData = () => {
        jsonViewer.open('Response', result);
    };

    const onClickDebugData = () => {
        jsonViewer.open('Response', result.debug_factor);
    };

    const onClickRetry = async (e: React.MouseEvent) => {
        e.preventDefault();
        await retryAnswer();
    };

    return (
        <div className="single-test-card">
            <div className="flex justify-between">
                <div className="single-test-card-head">
                    {query} 
                </div>

                <Popover>
                    {({ open, close }) => (
                        <>
                            <PopoverButton open={open} close={close} />
                            <PopoverMenu>
                                <PopoverItem onClick={onClickResponseData}
                                            className="px-5 py-2 flex items-center text-black-400 leading-[1.2]"
                                >
                                    {intl.formatMessage({ id: 'i00012' })}
                                </PopoverItem>
                                <PopoverItem onClick={onClickDebugData}
                                            className="px-5 py-2 flex items-center text-black-400 leading-[1.2]"
                                >
                                    {intl.formatMessage({ id: 'i00013' })}
                                </PopoverItem>
                            </PopoverMenu>
                        </>
                    )}
                </Popover>
            </div>
            <div className="single-test-card-subhead flex justify-end items-center">
                <button className="flex items-center gap-1"
                        onClick={onClickRetry}
                >
                    {intl.formatMessage({ id: "i00014" })}
                    <img src={RefreshIcon} alt="refresh"
                         className="size-4"
                    />
                </button>
            </div>
        </div>
    );
};

export default SingleTestPanel;
